import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Bot } from 'lucide-react';
import { useChat } from '../../hooks/useChat';
import ChatMessage from '../../components/ChatMessage/ChatMessage';
import ChatInput from '../../components/ChatInput/ChatInput';
import Suggestions from '../../components/Suggestions/Suggestions';
import ConversationSidebar from '../../components/ConversationSidebar/ConversationSidebar';
import { getUserId } from '../../services/userService';
import { AppContainer, Container, Messages, TypingIndicator, TypingDot } from './styles';

const ChatApp: React.FC = () => {
  const { sessionId: routeSessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const [userId] = useState(() => getUserId());
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const {
    messages,
    inputValue,
    setInputValue,
    isLoading,
    sessionId,
    handleSendMessage,
    handleLocationShare,
    handleQuickAction,
    handleKeyPress,
    messagesEndRef,
  } = useChat(routeSessionId);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    window.parent.postMessage(
      {
        type: "NEARVANA_EVENT",
        source: "chat_app",
        payload: {
          type: "messages_updated",
          timestamp: new Date().toISOString(),
        }
      },
      "*"
    );
    scrollToBottom();
  }, [messages]);

  // Update URL when sessionId changes
  useEffect(() => {
    if (sessionId && `/chat/${sessionId}` !== window.location.pathname) {
      window.parent.postMessage(
        {
          type: "NEARVANA_EVENT",
          source: "chat_app",
          payload: {
            type: "session_id_updated",
            sessionId,
          }
        },
        "*"
      );
      navigate(`/chat/${sessionId}`, { replace: true });
    }
  }, [sessionId, navigate]);

  const handleSelectConversation = (selectedSessionId: string) => {
    navigate(`/chat/${selectedSessionId}`);
    setIsSidebarOpen(false);
  };

  const handleNewConversation = () => {
    // Clear session storage and navigate to /chat (will create new session)
    sessionStorage.removeItem('nia_session_id');
    navigate('/chat');
    setIsSidebarOpen(false);
    window.location.reload(); // Reload to reset state
  };

  return (
    <AppContainer>
      <ConversationSidebar
        userId={userId}
        currentSessionId={sessionId}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <Container $sidebarOpen={isSidebarOpen}>
        <Messages>
          {messages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              showActions={message.type === 'assistant' && false}
            />
          ))}

          {isLoading && (
            <div className="message assistant">
              <div className="message-avatar">
                <div className="ai-avatar">
                  <Bot size={16} />
                </div>
              </div>
              <TypingIndicator>
                <TypingDot />
                <TypingDot />
                <TypingDot />
              </TypingIndicator>
            </div>
          )}

          {messages.length === 1 && (
            <Suggestions onQuickAction={handleQuickAction} />
          )}

          <div ref={messagesEndRef} />
        </Messages>

        <ChatInput
          inputValue={inputValue}
          setInputValue={setInputValue}
          isLoading={isLoading}
          handleSendMessage={handleSendMessage}
          handleLocationShare={handleLocationShare}
          handleKeyPress={handleKeyPress}
        />
      </Container>
    </AppContainer>
  );
};

export default ChatApp;
